import React from 'react';
import { ArrowDownTrayIcon, PrinterIcon, LinkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { usePDFGeneration } from '../../hooks/usePDFGeneration';
import VerticalPreview from './components/VerticalPreview';
import HorizontalPreview from './components/HorizontalPreview';
import './components/TEST_STYLE.css'; // Import our custom messenger styles

export default function PDFPreviewLayout({ 
  book, 
  onPrint, 
  debugMode = false, 
  savedPdfUrls = { 
    cover: null, 
    verticalContent: null, 
    horizontalContent: null 
  },
  forceRefresh = false,
  previewOnly = false
}) {
  const { generatePDF, generateCoverPDF, generateInteriorPDF, generateVerticalInteriorPDF, isGenerating, error, savedPdfUrls: hookPdfUrls } = usePDFGeneration(book);
  const [refreshTimestamp, setRefreshTimestamp] = React.useState(Date.now());
  // Lightweight progress for visible feedback during downloads
  const [progress, setProgress] = React.useState({ vertical: null, horizontal: null });

  // Combine props and hook PDF URLs (prioritize hook values as they may be more recent)
  const combinedPdfUrls = previewOnly
    ? savedPdfUrls
    : {
        ...savedPdfUrls,
        ...hookPdfUrls
      };

  // Force refresh of PDF previews on mount and when status changes
  React.useEffect(() => {
    // Always set a fresh timestamp when the component mounts or when forceRefresh changes
    // This ensures we never show a cached PDF with incorrect dimensions
    setRefreshTimestamp(Date.now());
    
    // Set up a timer to refresh again after a short delay
    // This handles race conditions where the PDF might not be ready immediately
    const timer = setTimeout(() => {
      setRefreshTimestamp(Date.now());
    }, 500);
    
    return () => clearTimeout(timer);
  }, [forceRefresh, isGenerating]);
  
  // Additional refresh when book data changes or component mounts
  React.useEffect(() => {
    setRefreshTimestamp(Date.now());
  }, [book?.id]);

  // Add cache-busting parameter to PDF URLs
  const getCacheBustedUrl = (url) => {
    if (!url) return null;
    if (typeof url === 'string' && url.startsWith('blob:')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${refreshTimestamp}`;
  };

  const handleDownload = async (orientation, evt, opts = {}) => {
    // Silence noisy logs only when not in debug mode
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;
    try {
      const enableDebug = !!evt?.ctrlKey || debugMode;
      if (!enableDebug) {
        console.log = () => {};
        console.warn = () => {};
        // Keep errors visible to surface real failures
        console.error = originalError;
      }

      // Support modifier-click ranges to avoid heavy generation while debugging
      // Alt-click: first 4 pages; Shift-click: first spread (pages 1-2)
      const totalPages = parseInt(book?.totalPages || 0, 10) || 0;
      const rangeOpts = (() => {
        if (evt?.altKey) {
          return { pageStart: 1, pageEnd: Math.min(4, totalPages) };
        }
        if (evt?.shiftKey) {
          return { pageStart: 1, pageEnd: Math.min(2, totalPages) };
        }
        // Default: full book on normal click
        return { pageStart: 1, pageEnd: totalPages };
      })();

      // Default: include images. Ctrl+Alt can force omitImages for emergency fallback.
      const extraOpts = {};
      const debugForceOmit = !!(evt?.ctrlKey && evt?.altKey);
      const effectiveOpts = debugForceOmit ? { ...extraOpts, omitImages: true } : extraOpts;

      // Initialize progress UI with estimated total if available
      if (orientation === 'vertical') {
        const estTotal = (rangeOpts.pageStart && rangeOpts.pageEnd) ? (rangeOpts.pageEnd - rangeOpts.pageStart + 1) : totalPages;
        setProgress((p) => ({ ...p, vertical: { current: 0, total: estTotal } }));
      } else if (orientation === 'horizontal') {
        const pages = (rangeOpts.pageStart && rangeOpts.pageEnd) ? (rangeOpts.pageEnd - rangeOpts.pageStart + 1) : totalPages;
        const estSpreads = Math.max(1, Math.ceil(pages / 2));
        setProgress((p) => ({ ...p, horizontal: { current: 0, total: estSpreads } }));
      }

      console.info(`[PDF] Starting ${orientation} download`, { orientation, debug: enableDebug });
      let resp;
      if (orientation === 'horizontal') {
        resp = await generateInteriorPDF(false, { 
          debug: enableDebug, 
          returnBlob: true, 
          onProgress: ({ current, total }) => setProgress((p) => ({ ...p, horizontal: { current, total } })),
          ...rangeOpts,
          ...effectiveOpts 
        });
      } else if (orientation === 'vertical') {
        // Always single-range generation (full book by default)
        resp = await generateVerticalInteriorPDF(false, { 
          debug: enableDebug, 
          returnBlob: true, 
          onProgress: ({ current, total }) => setProgress((p) => ({ ...p, vertical: { current, total } })),
          ...rangeOpts,
          ...effectiveOpts 
        });
      } else {
        resp = await generatePDF(orientation, false, { debug: enableDebug, returnBlob: true, ...rangeOpts, ...effectiveOpts });
      }

      if (resp && typeof resp === 'object' && 'blob' in resp && resp.blob) {
        const url = URL.createObjectURL(resp.blob);
        const link = document.createElement('a');
        const base = book?.title || 'book';
        link.href = url;
        link.download = `${base}-${orientation}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.info(`[PDF] ${orientation} download complete`);
        // Clear progress after a short delay so the user can see 100%
        setTimeout(() => setProgress((p) => ({ ...p, [orientation]: null })), 500);
      } else {
        // Fallback: attempt built-in download path
        let ok = false;
        try {
          let res;
          if (orientation === 'horizontal') {
            res = await generateInteriorPDF(false, { 
              debug: enableDebug, 
              onProgress: ({ current, total }) => setProgress((p) => ({ ...p, horizontal: { current, total } })),
              ...rangeOpts,
              ...effectiveOpts 
            });
          } else if (orientation === 'vertical') {
            res = await generateVerticalInteriorPDF(false, { 
              debug: enableDebug, 
              onProgress: ({ current, total }) => setProgress((p) => ({ ...p, vertical: { current, total } })),
              ...rangeOpts,
              ...effectiveOpts 
            });
          } else {
            res = await generatePDF(orientation, false, { debug: enableDebug, ...rangeOpts, ...effectiveOpts });
          }
          ok = res === true || (res && typeof res === 'object');
        } catch (e) {
          console.error(e);
        }
        if (!ok) {
          alert('Failed to generate PDF. Please check the console for details.');
        }
      }
    } finally {
      // Restore console methods
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
      // Ensure progress clears once generation stops (if not already cleared)
      setTimeout(() => setProgress((p) => ({ ...p, [orientation]: null })), 1000);
    }
  };

  const handleRegenerate = async (pdfType) => {
    if (isGenerating) return;
    
    try {
      if (pdfType === 'verticalContent') {
        await generateVerticalInteriorPDF(true);
      } else if (pdfType === 'horizontalContent') {
        await generateInteriorPDF(true);
      } else if (pdfType === 'cover') {
        await generateCoverPDF(true);
      }
    } catch (err) {
      console.error('Error regenerating PDF:', err);
      alert('Failed to regenerate PDF. Please try again.');
    }
  };

  // Pass the cover URL to HorizontalPreview through book object
  const bookWithCoverUrl = {
    ...book,
    pdfUrls: {
      ...book?.pdfUrls,
      cover: combinedPdfUrls?.cover ? getCacheBustedUrl(combinedPdfUrls.cover) : null,
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4 sm:px-6 lg:px-8">
        {/* Vertical PDF Preview */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Single Page View (Interior)
            </h2>
            <div className="flex space-x-2">
              {combinedPdfUrls?.verticalContent && (
                <>
                  <a
                    href={getCacheBustedUrl(combinedPdfUrls.verticalContent)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {previewOnly ? 'Open Preview' : 'View Saved PDF'}
                  </a>
                  {!previewOnly && (
                    <button
                      onClick={() => handleRegenerate('verticalContent')}
                      disabled={isGenerating}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isGenerating ? (
                        <>
                          <div className="animate-spin h-4 w-4 mr-1 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-1" />
                          Regenerate
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
              <button
                onClick={(e) => handleDownload('vertical', e)}
                disabled={isGenerating}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full"></div>
                    {progress.vertical ? (
                      <>
                        Generating... ({progress.vertical.current}/{progress.vertical.total})
                      </>
                    ) : (
                      <>Generating...</>
                    )}
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Download
                  </>
                )}
              </button>
              {progress.vertical && (
                <span className="ml-2 text-xs text-gray-500">{progress.vertical.current}/{progress.vertical.total}</span>
              )}
            </div>
          </div>
          <div className="rounded-lg shadow-md border border-gray-200 bg-white p-4">
            {/* Always show continuous design preview for better UX */}
            <VerticalPreview book={book} skipCover={true} continuous={true} />
          </div>
        </div>

        {/* Horizontal PDF Preview (includes cover as first page) */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Double Page View (Interior)
            </h2>
            <div className="flex space-x-2">
              {combinedPdfUrls?.horizontalContent && (
                <>
                  <a
                    href={getCacheBustedUrl(combinedPdfUrls.horizontalContent)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" />
                    {previewOnly ? 'Open Preview' : 'View Saved PDF'}
                  </a>
                  {!previewOnly && (
                    <button
                      onClick={() => handleRegenerate('horizontalContent')}
                      disabled={isGenerating}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isGenerating ? (
                        <>
                          <div className="animate-spin h-4 w-4 mr-1 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-1" />
                          Regenerate
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
              <button
                onClick={(e) => handleDownload('horizontal', e)}
                disabled={isGenerating}
                className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin h-4 w-4 mr-1 border-2 border-white border-t-transparent rounded-full"></div>
                    {progress.horizontal ? (
                      <>
                        Generating... ({progress.horizontal.current}/{progress.horizontal.total})
                      </>
                    ) : (
                      <>Generating...</>
                    )}
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                    Download
                  </>
                )}
              </button>
              
              {/* Add cover regenerate button directly here */}
              {!previewOnly && combinedPdfUrls?.cover && (
                <button
                  onClick={() => handleRegenerate('cover')}
                  disabled={isGenerating}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin h-4 w-4 mr-1 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      Regenerate Cover (Exterior)
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          <div className="rounded-lg shadow-md border border-gray-200 bg-white p-4">
            {combinedPdfUrls?.horizontalContent ? (
              <div className="w-full h-[500px]">
                <iframe 
                  src={getCacheBustedUrl(combinedPdfUrls.horizontalContent)} 
                  className="w-full h-full"
                  title="Double Page PDF (Interior)"
                  style={{ border: 'none' }}
                />
              </div>
            ) : (
              <HorizontalPreview book={bookWithCoverUrl} debugMode={debugMode} skipCover={true} continuous={true} />
            )}
          </div>
        </div>
      </div>

      {/* Print Button (only if onPrint function is provided and not in preview-only mode) */}
      {onPrint && !previewOnly && (
        <div className="flex justify-center mt-8">
          <button
            onClick={onPrint}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Continue to Print & Ship
          </button>
        </div>
      )}

      {/* Debug Information when debug mode is enabled */}
      {debugMode && (
        <pre className="mt-8 p-4 border border-gray-300 rounded-md bg-gray-50 overflow-auto text-xs">
          {JSON.stringify({ bookId: book?.id, error, savedPdfUrls: combinedPdfUrls }, null, 2)}
        </pre>
      )}
    </div>
  );
}
